/**
 * MagicBox — the animated 3D gift-box scene, LIGHT + brand-themed.
 *
 * Presentational: the parent owns the surprise state machine and flips
 * `open` to true once the prize is reserved; MagicBox plays the tremble →
 * lid-launch → burst sequence and reveals the `prize` slot.
 *
 * Effects (all respect prefers-reduced-motion):
 *   - moving background shades (CSS drifting bokeh)
 *   - ambient brand-tinted motes (canvas)
 *   - celebratory confetti burst on open (canvas)
 *   - orbiting particle aura around the prize card (canvas, depth-sorted)
 *   - floating glyphs that pop from the box on open (DOM + WAAPI)
 *   - perpetual gentle card float (CSS)
 *
 * Accent colors come from the batch brand via the `theme` prop and are set
 * as CSS custom properties; canvas particles use the rgb strings.
 */
import React, { useEffect, useRef, useState } from 'react';
import './MagicBox.css';

export interface MagicTheme {
  accent: string;
  accent2: string;
  accentSoft: string;
  accentDeep: string;
  /** "r,g,b" for canvas fills. */
  accentRgb: string;
  accent2Rgb: string;
  btnText: string;
}

interface MagicBoxProps {
  open: boolean;
  theme?: MagicTheme;
  sealSrc?: string | null;
  prize?: React.ReactNode;
  hint?: React.ReactNode;
  onBoxClick?: () => void;
}

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const GLYPHS = ['✦', '✧', '❋', '✺', '✷', '⋆', '✴︎'];

const MagicBox: React.FC<MagicBoxProps> = ({ open, theme, sealSrc, prize, hint, onBoxClick }) => {
  const motesRef = useRef<HTMLCanvasElement | null>(null);
  const burstRef = useRef<HTMLCanvasElement | null>(null);
  const auraBackRef = useRef<HTMLCanvasElement | null>(null);
  const auraFrontRef = useRef<HTMLCanvasElement | null>(null);
  const prizeRef = useRef<HTMLDivElement | null>(null);
  const glyphLayerRef = useRef<HTMLDivElement | null>(null);
  const [trembling, setTrembling] = useState(false);
  const [opened, setOpened] = useState(false);

  const accentRgb = theme?.accentRgb || '196,90,32';
  const accent2Rgb = theme?.accent2Rgb || '212,112,46';

  const styleVars = theme
    ? ({
        '--mb-accent': theme.accent,
        '--mb-accent-2': theme.accent2,
        '--mb-accent-soft': theme.accentSoft,
        '--mb-accent-deep': theme.accentDeep,
        '--mb-btn-text': theme.btnText,
      } as React.CSSProperties)
    : undefined;

  // ---- ambient brand-tinted motes (always) ----
  useEffect(() => {
    const canvas = motesRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    /* v8 ignore start -- canvas drawing; jsdom has no 2D context. */
    let raf = 0;
    let motes: Array<{ x: number; y: number; vy: number; r: number; p: number; c: string }> = [];
    const make = () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vy: -(Math.random() * 0.25 + 0.08),
      r: Math.random() * 2 + 0.8,
      p: Math.random() * Math.PI * 2,
      c: Math.random() < 0.5 ? accentRgb : accent2Rgb,
    });
    const size = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      motes = Array.from({ length: Math.max(16, Math.round(canvas.width / 36)) }, make);
    };
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const m of motes) {
        m.y += m.vy; m.p += 0.03; m.x += Math.sin(m.p) * 0.25;
        if (m.y < -10) { Object.assign(m, make(), { y: canvas.height + 10 }); }
        const a = 0.18 + Math.sin(m.p) * 0.14;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${m.c},${Math.max(0, a)})`;
        ctx.shadowBlur = 6; ctx.shadowColor = `rgba(${m.c},.35)`;
        ctx.fill(); ctx.shadowBlur = 0;
      }
      raf = window.requestAnimationFrame(draw);
    };
    size();
    window.addEventListener('resize', size);
    raf = window.requestAnimationFrame(draw);
    return () => { window.cancelAnimationFrame(raf); window.removeEventListener('resize', size); };
    /* v8 ignore stop */
  }, [accentRgb, accent2Rgb]);

  // ---- confetti burst, fired once when the box opens ----
  const fireBurst = () => {
    const canvas = burstRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    /* v8 ignore start -- canvas particle animation; not executable in jsdom. */
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const cx = canvas.width / 2;
    const cy = canvas.height * 0.46;
    // brand colors + a couple festive contrasts that read on light
    const colors = [accentRgb, accent2Rgb, '255,122,89', '94,200,255', '120,72,16'];
    const parts = Array.from({ length: 150 }, () => {
      const ang = Math.random() * Math.PI * 2;
      const spd = Math.random() * 9 + 2.5;
      return {
        x: cx, y: cy, vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd - 4,
        g: 0.12 + Math.random() * 0.08, r: Math.random() * 2.8 + 1.2,
        life: 1, decay: 0.006 + Math.random() * 0.01,
        c: colors[(Math.random() * colors.length) | 0], spin: Math.random() * Math.PI * 2,
      };
    });
    const step = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      for (const p of parts) {
        if (p.life <= 0) continue;
        alive = true;
        p.vy += p.g; p.x += p.vx; p.y += p.vy; p.vx *= 0.99; p.life -= p.decay; p.spin += 0.2;
        ctx.save();
        ctx.translate(p.x, p.y); ctx.rotate(p.spin);
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = `rgba(${p.c},1)`;
        ctx.shadowBlur = 4; ctx.shadowColor = 'rgba(120,72,16,.25)';
        ctx.fillRect(-p.r / 2, -p.r * 2, p.r, p.r * 4);
        ctx.fillRect(-p.r * 2, -p.r / 2, p.r * 4, p.r);
        ctx.restore();
      }
      if (alive) window.requestAnimationFrame(step);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    window.requestAnimationFrame(step);
    /* v8 ignore stop */
  };

  // ---- floating glyphs that pop from the box on open ----
  const popGlyphs = () => {
    const layer = glyphLayerRef.current;
    if (!layer) return;
    /* v8 ignore start -- WAAPI DOM animation; not meaningfully testable in jsdom. */
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight * 0.46;
    for (let i = 0; i < 11; i++) {
      const el = document.createElement('div');
      el.className = 'mb-glyph';
      el.textContent = GLYPHS[i % GLYPHS.length];
      const ang = (i / 11) * Math.PI * 2;
      const dist = 90 + Math.random() * 90;
      const dx = Math.cos(ang) * dist;
      const rise = -(120 + Math.random() * 150);
      el.style.left = `${cx}px`;
      el.style.top = `${cy}px`;
      el.style.fontSize = `${16 + Math.random() * 16}px`;
      layer.appendChild(el);
      if (typeof el.animate === 'function') {
        const anim = el.animate(
          [
            { transform: 'translate(-50%,-50%) scale(.2) rotate(0deg)', opacity: 0 },
            { transform: `translate(calc(-50% + ${dx * 0.6}px), calc(-50% + ${rise * 0.4}px)) scale(1.1) rotate(90deg)`, opacity: 1, offset: 0.35 },
            { transform: `translate(calc(-50% + ${dx * 1.3}px), calc(-50% + ${rise}px)) scale(.7) rotate(200deg)`, opacity: 0 },
          ],
          { duration: 1400 + Math.random() * 1200, delay: 300 + Math.random() * 450, easing: 'cubic-bezier(.2,.8,.3,1)', fill: 'forwards' },
        );
        anim.onfinish = () => el.remove();
      } else {
        el.remove();
      }
    }
    /* v8 ignore stop */
  };

  // ---- orbiting particle aura around the prize card (while opened) ----
  useEffect(() => {
    if (!opened) return;
    const back = auraBackRef.current;
    const front = auraFrontRef.current;
    const bx = back?.getContext('2d');
    const fx = front?.getContext('2d');
    if (!back || !front || !bx || !fx) return;
    /* v8 ignore start -- canvas orbit animation; not executable in jsdom. */
    let raf = 0;
    const colors = [accentRgb, accent2Rgb, '255,122,89', '120,72,16'];
    const size = () => {
      for (const c of [back, front]) { c.width = window.innerWidth; c.height = window.innerHeight; }
    };
    const orbiters = Array.from({ length: 16 }, (_, i) => ({
      a: Math.random() * Math.PI * 2,
      spd: (Math.random() * 0.4 + 0.4) * (Math.random() < 0.5 ? 1 : -1) * 0.018,
      rad: 0.82 + Math.random() * 0.45,
      sz: Math.random() * 1.6 + 1.4,
      c: colors[i % colors.length],
      trail: [] as Array<{ x: number; y: number; depth: number }>,
    }));
    const clear = () => { bx.clearRect(0, 0, back.width, back.height); fx.clearRect(0, 0, front.width, front.height); };
    const dot = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: string, alpha: number) => {
      ctx.save();
      ctx.globalAlpha = Math.max(0, alpha);
      ctx.fillStyle = `rgba(${c},1)`;
      ctx.shadowBlur = 6; ctx.shadowColor = 'rgba(120,72,16,.3)';
      ctx.beginPath(); ctx.arc(x, y, Math.max(0.4, r), 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    };
    const draw = () => {
      raf = window.requestAnimationFrame(draw);
      clear();
      const el = prizeRef.current;
      const r = el ? el.getBoundingClientRect() : null;
      if (!r || r.width === 0) return;
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      const rx = r.width / 2 + 44, ry = r.height / 2 + 34;
      for (const o of orbiters) {
        o.a += o.spd;
        const depth = (Math.sin(o.a) + 1) / 2;
        const x = cx + Math.cos(o.a) * rx * o.rad;
        const y = cy + Math.sin(o.a) * ry * o.rad;
        o.trail.unshift({ x, y, depth });
        if (o.trail.length > 7) o.trail.pop();
        for (let t = o.trail.length - 1; t >= 1; t--) {
          const p = o.trail[t];
          const fade = 1 - t / o.trail.length;
          dot(p.depth > 0.5 ? fx : bx, p.x, p.y, o.sz * (0.4 + p.depth) * fade, o.c, (0.12 + p.depth * 0.4) * fade * fade);
        }
        dot(depth > 0.5 ? fx : bx, x, y, o.sz * (0.5 + depth * 1.1), o.c, 0.5 + depth * 0.5);
      }
    };
    size();
    window.addEventListener('resize', size);
    raf = window.requestAnimationFrame(draw);
    return () => { window.cancelAnimationFrame(raf); window.removeEventListener('resize', size); clear(); };
    /* v8 ignore stop */
  }, [opened, accentRgb, accent2Rgb]);

  // ---- orchestrate the open sequence when `open` flips true ----
  useEffect(() => {
    if (!open) {
      setOpened(false);
      setTrembling(false);
      return;
    }
    if (opened) return;
    const reduce = prefersReducedMotion();
    if (reduce) {
      setOpened(true);
      popGlyphs();
      return;
    }
    setTrembling(true);
    const t = window.setTimeout(() => {
      setTrembling(false);
      setOpened(true);
      fireBurst();
      popGlyphs();
    }, 620);
    return () => window.clearTimeout(t);
  }, [open, opened]);

  return (
    <div className="mb-root" data-testid="magic-box" style={styleVars}>
      <div className="mb-shades" aria-hidden="true">
        <div className="mb-shade s1" />
        <div className="mb-shade s2" />
        <div className="mb-shade s3" />
      </div>
      <canvas className="mb-motes" ref={motesRef} aria-hidden="true" />
      <canvas className="mb-aura-back" ref={auraBackRef} aria-hidden="true" />

      <div className="mb-stage">
        <div className={`mb-scene${opened ? ' mb-opened' : ''}`}>
          <div className="mb-glow-floor" />
          <div className="mb-light-shaft" />

          <div
            className={`mb-box${trembling ? ' mb-trembling' : ''}`}
            onClick={!opened && onBoxClick ? onBoxClick : undefined}
            role={!opened && onBoxClick ? 'button' : undefined}
            aria-label={!opened && onBoxClick ? 'Open your gift' : undefined}
          >
            <div className="mb-gift">
              <div className="mb-face mb-wall mb-front" />
              <div className="mb-face mb-wall mb-back" />
              <div className="mb-face mb-wall mb-left" />
              <div className="mb-face mb-wall mb-right" />
              <div className="mb-face mb-floor" />
            </div>
            <div className="mb-lid">
              <div className="mb-lid-skirt mb-front" />
              <div className="mb-lid-skirt mb-back" />
              <div className="mb-lid-skirt mb-left" />
              <div className="mb-lid-skirt mb-right" />
              <div className="mb-lid-top" />
              {sealSrc && <img className="mb-seal" src={sealSrc} alt="" aria-hidden="true" />}
            </div>
          </div>
        </div>
      </div>

      <canvas className="mb-burst" ref={burstRef} aria-hidden="true" />
      <div className="mb-glyph-layer" ref={glyphLayerRef} aria-hidden="true" />

      {/* Prize card — visible once opened (aura orbits this element) */}
      <div className={`mb-prize${opened ? ' mb-show' : ''}`} ref={prizeRef} data-testid="magic-prize">
        {opened ? prize : null}
      </div>

      <canvas className="mb-aura-front" ref={auraFrontRef} aria-hidden="true" />

      {/* Tag / CTA — visible while closed */}
      <div className={`mb-hint${opened ? ' mb-dim' : ''}`}>
        {!opened ? hint : null}
      </div>
    </div>
  );
};

export default MagicBox;
